import { useNavigate, useParams, useSearchParams } from "react-router";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import GridTable from "../../../components/GridTable/GridTable";
import endpoints from "../../../services/endpoints";
import { useDispatch } from 'react-redux';
import { setPreviousUrl } from "../../../store/slices/geolocationSlice";
import { setSelectedBankId, setListOfBankAccountsUrl } from '../../../store/slices/selectedBankIdSlice';
import { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router";

export default function ListOfBankAccounts({ group_type, group }: { group_type: string; group: string }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
    const [searchPlaceholder, setSearchPlaceholder] = useState<string>("");
    const { bankAccounts } = endpoints;
    const date = searchParams.get('date');
    const dispatch = useDispatch();

    useEffect(() => {
      dispatch(setListOfBankAccountsUrl(location.pathname + location.search));
    }, [location.pathname, location.search, dispatch]);

    // Define placeholders for rotating
    const SEARCH_PLACEHOLDERS = [
        "Search by user name",
        "Search by user code"
    ];

    // Set initial placeholder based on group_type
    useEffect(() => {
        if (group_type === "Account Receivable (Customers) GL" || group_type === "ZoodPay/Parent Payable") {
            setSearchPlaceholder(SEARCH_PLACEHOLDERS[0]);
        } else if (group_type === "Cash in Hand") {
            setSearchPlaceholder("Search by regional office name");
        } else if (group_type === "Bank Accounts" || group_type === "Bank Charges") {
            setSearchPlaceholder("Search by bank name");
        } else if (
            group_type === "Bundle Activation MNO Inventory GL" ||
            group_type === "Etopup MNO Inventory GL" ||
            group_type === "Bundle Activation MNO Payable GL" ||
            group_type === "Etopup MNO Payable GL"
        ) {
            setSearchPlaceholder("Search by mno name");
        } else if (group_type === "Mobile Money") {
            setSearchPlaceholder("Search by money transfer platform");
        } else if (group_type === "Etopup Service Fee Revenue" || group_type === "Bundle Activation Service Fee Revenue") {
            setSearchPlaceholder("Search by mno name");
        } else if (group_type === "Service Fee Expense") {
            setSearchPlaceholder("Search by user name and code");
        }
    }, [group_type]);

    // Only rotate for Account Receivable (Customers) GL
    const rotatePlaceholder = useCallback(() => {
        if (group_type !== "Account Receivable (Customers) GL" && group_type !== "ZoodPay/Parent Payable") return;
        setIsTransitioning(true);
        setTimeout(() => {
            setSearchPlaceholder(prev => {
                const currentIndex = SEARCH_PLACEHOLDERS.indexOf(prev);
                const nextIndex = (currentIndex + 1) % SEARCH_PLACEHOLDERS.length;
                return SEARCH_PLACEHOLDERS[nextIndex];
            });
            setIsTransitioning(false);
        }, 500);
    }, [group_type]);

    useEffect(() => {
        if (group_type !== "Account Receivable (Customers) GL" && group_type !== "ZoodPay/Parent Payable") return;
        const interval = setInterval(rotatePlaceholder, 2500);
        return () => clearInterval(interval);
    }, [rotatePlaceholder, group_type]);

    const tableConfigs: any = {
        "Cash in Hand": {
            "Regional Office Name": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Address": {
                id: "location",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Bank Accounts": {
            "Bank Name": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Bank Charges": {
            "Bank Name": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Account Receivable (Customers) GL": {
            "User Name (User Code)": {
                id: "name",
                custom: ["user_name"],
                className: "min-w-[300px]",
            },
            "User Location": {
                id: "location",
                custom: ["location"],
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Bundle Activation MNO Inventory GL": {
            "MNO Name": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Etopup MNO Inventory GL": {
            "MNO Name": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "ZoodPay/Parent Payable": {
            "User Name (User Code)": {
                id: "name",
                custom: ["user_name"],
                className: "min-w-[300px]",
            },
            "Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Mobile Money": {
            "Money Transfer Platform": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Etopup MNO Payable GL": {
            "MNO Name": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Bundle Activation MNO Payable GL": {
            "MNO Name": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Etopup Service Fee Revenue": {
            "MNO Name": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Bundle Activation Service Fee Revenue": {
            "MNO Name": {
                id: "name",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },
        "Service Fee Expense": {
            "User Name (User Code)": {
                id: "username",
                className: "min-w-[300px]",
            },
            "Running Balance": {
                id: "balance",
                custom: ["balance"],
                className: "min-w-[300px]",
            }
        },

    };

    const TableDetails = {
        ...(tableConfigs[group_type] || {}),
        Actions: {
            id: "actions",
            actionTypes: ["view"],
            className: "w-[100px]",
            disabledTooltip: "No transactions available for this account yet."
        }
    };

    const handleActions = async (id: string, key: string, text?: any) => {
        // Use id for the transactions API endpoint (URL navigation)
        const accountId = text?.id || id;
        
        // For Etopup MNO Inventory GL, use entity_id for external-wallet API
        // entity_id is the MNO ID needed for the external-wallet API call
        const bankIdForExternalApi = group_type === "Etopup MNO Inventory GL" 
            ? (text?.entity_id || accountId) 
            : accountId;
        
        dispatch(setSelectedBankId(bankIdForExternalApi));
        dispatch(setPreviousUrl(location.pathname + location.search));
        const updatedSearchParams = new URLSearchParams(searchParams);
        updatedSearchParams.delete('search');
        navigate(
            `/zoodpay-money/balance-sheet/${group_type.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-")}/${accountId}${updatedSearchParams.size !== 0 ? `?${updatedSearchParams}` : ""}`
        );
    };

    const staticText = {
        emptyScreenText: {
            heading1: "No Data",
            heading2: "No records found.",
        },
    };

    // Custom heading for Bank Charges
    const getHeading = () => {
        if (group_type === "Bank Charges") {
            return "List of Bank Charge Accounts";
        }
        return `List of ${group_type}`;
    };

    const getNavPath = () => {
        if (group_type === "Bank Charges") {
            return ["Zoodpay Money", "Balance Sheet", "List of Bank Charge Accounts"];
        }
        return ["Zoodpay Money", "Balance Sheet", `List of ${group_type}`];
    };

    return (
        <div>
            <Breadcrumbs
                navPath={getNavPath()}
                heading={getHeading()}
                links={[`/zoodpay-money/balance-sheet${date ? `?date=${date}` : ''}`, `/zoodpay-money/balance-sheet${date ? `?date=${date}` : ''}`]}
            />
            <GridTable
                staticText={staticText}
                mainEndPoint={bankAccounts}
                TableDetails={TableDetails}
                handleActions={(id: string, key: string, text?: string) => handleActions(id, key, text)}
                params={{ page_number: "1", group_type: group_type, group: group }}
                searchPlaceholder={searchPlaceholder}
                filters={false}
                exporting={true}
                reportType="Balance Sheet"
                // Excel export is enabled for all balance sheet lists
                placeholderClassName={`search-placeholder ${isTransitioning ? 'transitioning' : ''}`}
                notfoundRoute={{ state: { route: `/zoodpay-money/balance-sheet` } }}
                title={`View Transaction Details`}
                hasViewMDPrivilege={true}
                addMorePadding={false}
                mainDivScroll={true}
                customEmptyScreenHeight="h-64 w-42 lg:w-50 xl:w-58"
            />
        </div>
    );
}
